import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Collapse, Form, InputNumber, Modal, Progress, Radio, Select, Space, Switch, Tag, Typography, message } from 'antd';
import evmAdminService from '../../../shared/services/evmAdminService';

const { Text } = Typography;

const STATUS = { 2: ['排队中', 'default'], 3: ['迁移中', 'processing'], 4: ['失败', 'error'], 5: ['完成', 'success'] };
const KIND = { live: '在线迁移', cold: '冷迁移', ha: '故障重启' };
const fmtBytes = (b) => {
    if (b === null || b === undefined) return '-';
    const u = ['B', 'KB', 'MB', 'GB', 'TB'];
    let v = Number(b);
    let i = 0;
    while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(i ? 1 : 0)} ${u[i]}`;
};
const errMsg = (res, fallback) => res?.msg || res?.error || fallback;

// 迁移状态面板：每 2 秒轮询，进行中可取消（仅在线迁移）
const MigrateStatus = ({ vmId, onDone }) => {
    const [st, setSt] = useState(null);
    const [cancelling, setCancelling] = useState(false);
    const doneRef = useRef(false);

    const poll = useCallback(async () => {
        const res = await evmAdminService.getMigrateStatus(vmId);
        if (res?.status !== 'success') return;
        setSt(res.data || null);
        const s = Number(res.data?.status);
        if ((s === 4 || s === 5) && !doneRef.current) { doneRef.current = true; onDone?.(); }
    }, [vmId, onDone]);

    useEffect(() => {
        poll();
        const t = setInterval(poll, 2000);
        return () => clearInterval(t);
    }, [poll]);

    if (!st) return <Text type="secondary">加载中…</Text>;
    const s = Number(st.status);
    const [label, color] = STATUS[s] || [String(st.status), 'default'];
    const p = st.progress || {};
    const pct = p.data_total ? Math.min(100, Math.round((p.data_processed || 0) / p.data_total * 100)) : (s === 5 ? 100 : 0);
    const running = s === 2 || s === 3;

    const cancel = async () => {
        setCancelling(true);
        const res = await evmAdminService.cancelMigrate(vmId);
        setCancelling(false);
        if (res?.status === 'success') { message.success(res.msg || '已请求取消'); poll(); } else message.error(errMsg(res, '取消失败'));
    };

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <Space>
                <Tag color={color}>{label}</Tag>
                <Text type="secondary">{KIND[st.kind] || st.kind}</Text>
                {st.cancel_requested && running && <Tag>取消中</Tag>}
            </Space>
            <Progress percent={pct} status={s === 4 ? 'exception' : (running ? 'active' : undefined)} />
            {p.data_total ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                    {fmtBytes(p.data_processed)} / {fmtBytes(p.data_total)}
                    {p.iteration ? ` · 第 ${p.iteration} 轮` : ''}
                    {p.throttle ? ` · 限速 ${p.throttle}%` : ''}
                </Text>
            ) : null}
            {st.msg ? <Text type={s === 4 ? 'danger' : undefined}>{st.msg}</Text> : null}
            {s === 5 && st.downtime_ms !== null && st.downtime_ms !== undefined && <Text>停机 {st.downtime_ms} ms</Text>}
            {st.kind === 'live' && running && (
                <Button danger size="small" loading={cancelling} disabled={st.cancel_requested} onClick={cancel}>取消迁移</Button>
            )}
        </Space>
    );
};

const MigrateModal = ({ vm, open, onClose, onChanged }) => {
    const [form] = Form.useForm();
    const [mode, setMode] = useState('cold');
    const [pre, setPre] = useState(null);
    const [loadingPre, setLoadingPre] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [watching, setWatching] = useState(false);
    const defaultsSet = useRef(false);
    const vmId = vm?.id;

    // 打开时：迁移中直接看进度，否则按电源状态选默认模式
    useEffect(() => {
        if (!open || !vm) return;
        if (vm.status !== 'migrate') form.resetFields();
        setPre(null);
        defaultsSet.current = false;
        setWatching(vm.status === 'migrate');
        setMode(vm.power_state === 'running' ? 'live' : 'cold');
    }, [open, vm, form]);

    useEffect(() => {
        if (!open || !vmId || watching) return undefined;
        let alive = true;
        setLoadingPre(true);
        evmAdminService.getMigratePreflight(vmId, mode).then((res) => {
            if (!alive) return;
            setLoadingPre(false);
            if (res?.status !== 'success') { message.error(errMsg(res, '加载失败')); return; }
            const d = res.data || {};
            setPre(d);
            if (mode === 'live' && !d.live_supported) setMode('cold');
            const cur = form.getFieldValue('node_id');
            if (cur && !(d.targets || []).some((t) => t.node_id === cur && !t.reason)) form.setFieldsValue({ node_id: undefined });
            if (d.defaults && !defaultsSet.current) { defaultsSet.current = true; form.setFieldsValue({ options: d.defaults }); }
        });
        return () => { alive = false; };
    }, [open, vmId, mode, watching, form]);

    const submit = async () => {
        const v = await form.validateFields();
        const body = { node_id: v.node_id, mode };
        if (mode === 'live') body.options = { ...(pre?.defaults || {}), ...(form.getFieldValue('options') || {}) };
        setSubmitting(true);
        const res = await evmAdminService.startMigrate(vmId, body);
        setSubmitting(false);
        if (res?.status === 'success') {
            message.success(res.msg || '迁移已提交');
            setWatching(true);
            onChanged?.();
        } else message.error(errMsg(res, '迁移失败'));
    };

    const liveOk = pre ? pre.live_supported : true;
    const disabledAll = pre && pre.enabled === false;

    return (
        <Modal
            title={`迁移 ${vm ? (vm.name || vm.hostname || `#${vm.id}`) : ''}`}
            open={open}
            onCancel={onClose}
            destroyOnClose
            footer={watching
                ? [<Button key="close" onClick={onClose}>关闭</Button>]
                : [
                    <Button key="c" onClick={onClose}>取消</Button>,
                    <Button key="ok" type="primary" loading={submitting} disabled={disabledAll} onClick={submit}>迁移</Button>,
                ]}
        >
            {watching ? (
                <MigrateStatus vmId={vmId} onDone={onChanged} />
            ) : (
                <Form form={form} layout="vertical">
                    {disabledAll && <Alert type="warning" showIcon message="迁移功能未启用" style={{ marginBottom: 12 }} />}
                    <Form.Item label="方式">
                        <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)} optionType="button">
                            <Radio.Button value="live" disabled={!liveOk}>在线迁移</Radio.Button>
                            <Radio.Button value="cold">冷迁移</Radio.Button>
                        </Radio.Group>
                    </Form.Item>
                    <Form.Item name="node_id" label="目标节点" rules={[{ required: true, message: '请选择目标节点' }]}>
                        <Select
                            placeholder="选择目标节点"
                            loading={loadingPre}
                            showSearch
                            optionFilterProp="label"
                            options={(pre?.targets || []).map((t) => ({
                                value: t.node_id,
                                label: t.name,
                                disabled: !!t.reason,
                                title: t.reason || t.name,
                            }))}
                            optionRender={(o) => (
                                <Space direction="vertical" size={0}>
                                    <span>{o.label}</span>
                                    {o.data.disabled && <Text type="secondary" style={{ fontSize: 12 }}>{o.data.title}</Text>}
                                </Space>
                            )}
                        />
                    </Form.Item>
                    {mode === 'live' && (
                        <Collapse ghost size="small" items={[{
                            key: 'adv',
                            label: '高级',
                            forceRender: true,
                            children: (
                                <Space wrap size="middle" align="start">
                                    <Form.Item name={['options', 'bw_mbps']} label="带宽 (Mbps)" tooltip="0 = 不限">
                                        <InputNumber min={0} style={{ width: 120 }} />
                                    </Form.Item>
                                    <Form.Item name={['options', 'max_downtime_ms']} label="最大停机 (ms)">
                                        <InputNumber min={1} style={{ width: 120 }} />
                                    </Form.Item>
                                    <Form.Item name={['options', 'timeout']} label="超时 (s)">
                                        <InputNumber min={1} style={{ width: 120 }} />
                                    </Form.Item>
                                    <Form.Item name={['options', 'auto_converge']} label="自动收敛" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                    <Form.Item name={['options', 'postcopy']} label="Post-copy" valuePropName="checked" tooltip="内存脏页过快时切换为后拷贝">
                                        <Switch />
                                    </Form.Item>
                                    <Form.Item noStyle shouldUpdate={(a, b) => a.options?.postcopy !== b.options?.postcopy}>
                                        {({ getFieldValue }) => (
                                            <Form.Item name={['options', 'postcopy_after']} label="秒后切换">
                                                <InputNumber min={0} style={{ width: 120 }} disabled={!getFieldValue(['options', 'postcopy'])} />
                                            </Form.Item>
                                        )}
                                    </Form.Item>
                                </Space>
                            ),
                        }]} />
                    )}
                </Form>
            )}
        </Modal>
    );
};

export default MigrateModal;
